import { useCallback, useMemo, useState } from "react"
import dayjs from "dayjs"
import CharacteristicsService from "src/services/characteristics-service"
import { DATE_FORMAT } from "src/utils/constants"

const DEFAULT_STATE = {
    loading: false,
    data: null,
    hasResultado: false,
    puparData: [],
    puMtmData: [],
    dataReferencia: null,
}

const isValidDataReferencia = (value) => {
    if (!value || typeof value !== "string") return false
    if (value === "Invalid Date") return false
    return dayjs(value, DATE_FORMAT).isValid()
}

export const useMemoriaCalculo = () => {
    const service = useMemo(() => new CharacteristicsService(), [])
    const [state, setState] = useState(DEFAULT_STATE)

    const resolveDataReferencia = useCallback(async (cetipIsin) => {
        try {
            const response = await service.getDataMaisRecente(cetipIsin)
            const maisRecente = response?.body?.DataMaisRecente?.[0]
            const parsed = maisRecente ? dayjs(maisRecente) : null

            return parsed?.isValid() ? parsed.format(DATE_FORMAT) : dayjs().format(DATE_FORMAT)
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[useMemoriaCalculo] erro em getDataMaisRecente", error)
            return dayjs().format(DATE_FORMAT)
        }
    }, [service])

    const loadMemoriaCalculo = useCallback(async (cetipIsin, { dataReferencia } = {}) => {
        if (!cetipIsin) {
            setState(DEFAULT_STATE)
            return
        }

        setState((prev) => ({ ...prev, loading: true }))

        const resolvedDataReferencia = isValidDataReferencia(dataReferencia)
            ? dataReferencia
            : await resolveDataReferencia(cetipIsin)

        let parametros = null
        let resultado = null
        let puparRows = []
        let puMtmRows = []

        try {
            const parametrosResponse = await service.getParametersCalc(cetipIsin, resolvedDataReferencia)
            const parametrosBody = parametrosResponse?.body

            parametros = Array.isArray(parametrosBody) && parametrosBody.length > 0
                ? parametrosBody[0]
                : null
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[useMemoriaCalculo] erro em getParametersCalc", error)
        }

        try {
            const resultadoResponse = await service.getMemoryCalcResultPrice(cetipIsin, resolvedDataReferencia)
            const resultadoBody = resultadoResponse?.body

            // Só considera "resultado" se o endpoint realmente devolveu algo —
            // um array vazio [] não deve virar uma linha na tabela.
            resultado = Array.isArray(resultadoBody) && resultadoBody.length > 0
                ? resultadoBody[0]
                : null
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[useMemoriaCalculo] erro em getMemoryCalcResultPrice", error)
        }

        try {
            const [puparResponse, puMtmResponse] = await Promise.all([
                service.getMemoryCalcPUPAR(cetipIsin, resolvedDataReferencia),
                service.getMemoryCalcMTM(cetipIsin, resolvedDataReferencia),
            ])

            puparRows = Array.isArray(puparResponse?.body) ? puparResponse.body : []
            puMtmRows = Array.isArray(puMtmResponse?.body) ? puMtmResponse.body : []
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[useMemoriaCalculo] erro em getMemoryCalcPUPAR/getMemoryCalcMTM", error)
        }

        setState({
            loading: false,
            data: { ...resultado, ...parametros },
            hasResultado: Boolean(resultado),
            puparData: puparRows,
            puMtmData: puMtmRows,
            dataReferencia: resolvedDataReferencia,
        })
    }, [service, resolveDataReferencia])

    const exportExcel = useCallback((cetipIsin) => {
        if (!cetipIsin || !state.data) return

        return service.exportMemoryExcel(
            cetipIsin,
            state.data,
            state.puparData,
            state.puMtmData
        )
    }, [service, state])

    return {
        state,
        actions: {
            loadMemoriaCalculo,
            exportExcel,
        },
    }
}